export type HeatmapMetric = "contracts" | "commission" | "age";

export interface LegendStop {
  color: string;
  label: string;
}

export interface MetricLegendConfig {
  title: string;
  unit: string;
  type: "gradient" | "discrete";
  stops: LegendStop[];
}

/**
 * A ramp bucket = one visual class. `min` is the inclusive lower bound of the
 * data value that maps to `color`.
 *
 * Buckets are quantile-derived from the real distribution (12,222 locations),
 * NOT linear-to-max, because every metric is heavily right-skewed: contracts
 * have a median of 1 against a max of 542, so a linear ramp spends almost none
 * of its visual range on the 63% of locations that sit at the floor.
 */
export interface RampBucket {
  /** Inclusive lower bound in data units. */
  min: number;
  color: string;
  /** Legend text. Single source of truth, so legend and paint cannot drift. */
  label: string;
}

/**
 * Builds a Mapbox `step` expression from a ramp:
 *   step(input, firstColor, b1.min, b1.color, b2.min, b2.color, ...)
 * The first bucket's color is the base (everything below the second bucket).
 */
export function rampToStepExpression(
  input: unknown,
  buckets: readonly RampBucket[]
): unknown[] {
  const expr: unknown[] = ["step", input, buckets[0]!.color];
  for (const bucket of buckets.slice(1)) {
    expr.push(bucket.min, bucket.color);
  }
  return expr;
}

/** Legend stops derived from the exact same buckets the paint uses. */
export function rampToLegendStops(buckets: readonly RampBucket[]): LegendStop[] {
  return buckets.map((b) => ({ color: b.color, label: b.label }));
}

/** Contracts: bucket edges at the 63rd/79th/89th/95th percentiles. */
export const CONTRACTS_RAMP: readonly RampBucket[] = [
  { min: 1, color: "rgb(69,117,180)", label: "1" },
  { min: 2, color: "rgb(103,169,207)", label: "2\u20133" },
  { min: 4, color: "rgb(254,196,79)", label: "4\u20139" },
  { min: 10, color: "rgb(240,109,44)", label: "10\u201324" },
  { min: 25, color: "rgb(179,0,38)", label: "25+" },
];

/** Commission (EUR): bucket edges at p33/p56/p78/p93. */
export const COMMISSION_RAMP: readonly RampBucket[] = [
  { min: 0, color: "rgb(0,104,55)", label: "< 50 \u20ac" },
  { min: 50, color: "rgb(102,189,99)", label: "50\u201399 \u20ac" },
  { min: 100, color: "rgb(254,224,89)", label: "100\u2013199 \u20ac" },
  { min: 200, color: "rgb(244,109,67)", label: "200\u2013399 \u20ac" },
  { min: 400, color: "rgb(165,0,38)", label: "400 \u20ac+" },
];

/**
 * Median age per PLZ, in fixed 10-year buckets.
 *
 * Round decade boundaries beat quantile cuts here because the legend has to
 * read as plain German ("40\u201349 Jahre"). Verified against the 211 reliable
 * PLZ: 40 / 25 / 44 / 49 / 39 / 14 areas per bucket - no bucket empty or
 * dominant. Sequential (not diverging): this is a magnitude scale.
 *
 * Percentile trimming was tested and rejected. Once the placeholder ages are
 * excluded server-side, the observed domain is 26-79 years with a between-PLZ
 * SD of 14.4; trimming to p10-p90 would have discarded half that range.
 */
export const AGE_PLZ_RAMP: readonly RampBucket[] = [
  { min: 0, color: "rgb(255,245,235)", label: "< 30 J." },
  { min: 30, color: "rgb(253,208,162)", label: "30\u201339 J." },
  { min: 40, color: "rgb(253,141,60)", label: "40\u201349 J." },
  { min: 50, color: "rgb(230,85,13)", label: "50\u201359 J." },
  { min: 60, color: "rgb(166,54,3)", label: "60\u201369 J." },
  { min: 70, color: "rgb(102,37,6)", label: "70+ J." },
];

/** PLZ with fewer than 15 valid-age customers: shown grey, never coloured. */
export const PLZ_UNRELIABLE_COLOR = "rgb(203,213,225)";

export interface MetricConfig {
  id: HeatmapMetric;
  label: string;
  icon: string;
  description: string;
  // Layer visibility & paint rules
  heatmapVisible: boolean;
  circleVisibleAtAllZooms: boolean;
  heatmapPaint: {
    "heatmap-weight": any;
    "heatmap-intensity": any;
    "heatmap-color": any;
    "heatmap-radius": any;
    "heatmap-opacity": any;
  };
  circlePaint: {
    "circle-radius": any;
    "circle-color": any;
    "circle-stroke-color": any;
    "circle-stroke-width": any;
    /**
     * Mapbox's circle-opacity governs ONLY the fill; the stroke has its own
     * opacity that defaults to 1. Leaving it unset made every point render as
     * a fully-opaque white ring around an invisible fill below z15 - 12k of
     * those merged into a white sheet. It must always track circle-opacity.
     */
    "circle-stroke-opacity": any;
    "circle-opacity": any;
  };
  /** Whether the PLZ choropleth fill layer is shown for this metric. */
  plzFillVisible: boolean;
  /** Paint for the PLZ choropleth. Only the age metric supplies this. */
  fillPaint?: {
    "fill-color": any;
    "fill-opacity": any;
    "fill-outline-color": any;
  };
  legend: MetricLegendConfig;
}

export const METRIC_CONFIGS: Record<HeatmapMetric, MetricConfig> = {
  contracts: {
    id: "contracts",
    label: "Vertr\u00e4ge",
    icon: "HiDocumentText",
    description: "Vertragsdichte und Anzahl der Kundenabschl\u00fcsse pro Standort",
    heatmapVisible: true,
    circleVisibleAtAllZooms: false,
    plzFillVisible: false,
    heatmapPaint: {
      // The median location has customer_count 1. The old ramp (0->0, 10->1)
      // gave it weight 0.1, i.e. almost no density signal at all.
      "heatmap-weight": [
        "interpolate",
        ["linear"],
        ["get", "customer_count"],
        1, 0.35,
        3, 0.55,
        10, 0.8,
        25, 1,
      ],
      "heatmap-intensity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6, 1,
        11, 1.6,
        15, 2.4,
      ],
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        0, "rgba(69,117,180,0)",
        0.15, "rgba(69,117,180,0.75)",
        0.35, "rgb(103,169,207)",
        0.55, "rgb(254,196,79)",
        0.75, "rgb(240,109,44)",
        1, "rgb(179,0,38)",
      ],
      "heatmap-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        0, 4,
        9, 14,
        15, 26,
      ],
      // Hold across the mid-zoom band instead of decaying from z7, then hand
      // off sharply exactly where circle-opacity reaches full.
      "heatmap-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        7, 0.85,
        13, 0.85,
        15, 0,
      ],
    },
    circlePaint: {
      // Top stop at 100 (not the 542 max) so three-digit outliers clamp
      // instead of producing absurd discs.
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14,
        ["interpolate", ["linear"], ["get", "customer_count"], 1, 3.5, 3, 5, 10, 8, 25, 12, 100, 18],
        18,
        ["interpolate", ["linear"], ["get", "customer_count"], 1, 6, 3, 9, 10, 15, 25, 22, 100, 34],
      ],
      "circle-color": rampToStepExpression(["get", "customer_count"], CONTRACTS_RAMP),
      "circle-stroke-color": "rgba(15,23,42,0.55)",
      "circle-stroke-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        16, 0.5,
        18, 1,
      ],
      // Capped at 0.9 to match circle-opacity exactly: if the stroke ramps to
      // 1.0 while the fill stops at 0.9, the stroke leads the fill mid-fade
      // (at z14.5) - the same class of bug as the original white-out.
      "circle-stroke-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        15, 0.9,
      ],
      "circle-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        15, 0.9,
      ],
    },
    legend: {
      title: "Vertragsdichte",
      unit: "Vertr\u00e4ge",
      type: "discrete",
      stops: rampToLegendStops(CONTRACTS_RAMP),
    },
  },

  commission: {
    id: "commission",
    label: "Umsatz (Provision)",
    icon: "HiCurrencyEuro",
    description: "Provisionsvolumen pro Standort",
    heatmapVisible: true,
    circleVisibleAtAllZooms: false,
    plzFillVisible: false,
    heatmapPaint: {
      "heatmap-weight": [
        "interpolate",
        ["linear"],
        ["coalesce", ["get", "total_commission"], 0],
        0, 0.15,
        50, 0.35,
        150, 0.6,
        400, 0.85,
        1200, 1,
      ],
      "heatmap-intensity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6, 1,
        11, 1.6,
        15, 2.4,
      ],
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        0, "rgba(0,104,55,0)",
        0.15, "rgba(0,104,55,0.75)",
        0.35, "rgb(102,189,99)",
        0.55, "rgb(254,224,89)",
        0.75, "rgb(244,109,67)",
        1, "rgb(165,0,38)",
      ],
      "heatmap-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        0, 4,
        9, 14,
        15, 26,
      ],
      "heatmap-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        7, 0.85,
        13, 0.85,
        15, 0,
      ],
    },
    circlePaint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14,
        ["interpolate", ["linear"], ["coalesce", ["get", "total_commission"], 0], 0, 3.5, 100, 5.5, 300, 8, 800, 12, 1200, 16],
        18,
        ["interpolate", ["linear"], ["coalesce", ["get", "total_commission"], 0], 0, 6, 100, 9, 300, 14, 800, 20, 1200, 28],
      ],
      "circle-color": rampToStepExpression(
        ["coalesce", ["get", "total_commission"], 0],
        COMMISSION_RAMP
      ),
      "circle-stroke-color": "rgba(15,23,42,0.55)",
      "circle-stroke-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        16, 0.5,
        18, 1,
      ],
      // Capped at 0.9 to match circle-opacity exactly: if the stroke ramps to
      // 1.0 while the fill stops at 0.9, the stroke leads the fill mid-fade
      // (at z14.5) - the same class of bug as the original white-out.
      "circle-stroke-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        15, 0.9,
      ],
      "circle-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14, 0,
        15, 0.9,
      ],
    },
    legend: {
      title: "Provisionsvolumen",
      unit: "\u20ac",
      type: "discrete",
      stops: rampToLegendStops(COMMISSION_RAMP),
    },
  },

  age: {
    id: "age",
    label: "Kundenalter",
    icon: "HiUserGroup",
    description:
      "Medianalter je PLZ \u2013 nur Kunden mit Vertrag (Geburtsdatum wird erst bei Abschluss erfasst)",
    // The age metric is a PLZ choropleth: both point layers are hidden and the
    // fill layer carries the data. Per-address age dots were dropped because
    // 71.7% of them showed a single customer's age, not an area average.
    heatmapVisible: false,
    circleVisibleAtAllZooms: false,
    plzFillVisible: true,
    heatmapPaint: {
      "heatmap-weight": 0,
      "heatmap-intensity": 0,
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        0, "rgba(0,0,0,0)",
        1, "rgba(0,0,0,0)",
      ],
      "heatmap-radius": 1,
      "heatmap-opacity": 0,
    },
    circlePaint: {
      "circle-radius": 0,
      "circle-color": "rgba(0,0,0,0)",
      "circle-stroke-color": "rgba(0,0,0,0)",
      "circle-stroke-width": 0,
      "circle-stroke-opacity": 0,
      "circle-opacity": 0,
    },
    fillPaint: {
      "fill-color": [
        "case",
        ["!", ["get", "is_reliable"]],
        PLZ_UNRELIABLE_COLOR,
        rampToStepExpression(["get", "median_age"], AGE_PLZ_RAMP),
      ],
      "fill-opacity": 0.72,
      "fill-outline-color": "rgba(15,23,42,0.35)",
    },
    legend: {
      title: "Medianalter je PLZ",
      unit: "Jahre",
      type: "discrete",
      stops: [
        ...rampToLegendStops(AGE_PLZ_RAMP),
        { color: PLZ_UNRELIABLE_COLOR, label: "Zu wenig Daten (< 15)" },
      ],
    },
  },
};
